import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    MapPinIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    XCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EllipsisVerticalIcon,
    CheckCircleIcon,
    ClockIcon,
    StarIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getTrips, cancelTrip, forceCompleteTrip } from "../api/rideBookingsTripsApi";
import { extractApiError } from "@/lib/api-error";
import type { TripsQueryParams, TripStatus } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: TripStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "PENDING", label: "Pending" },
    { value: "STARTED", label: "Started" },
    { value: "DRIVER_ARRIVED", label: "Driver Arrived" },
    { value: "PICKED_UP", label: "Picked Up" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        STARTED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        DRIVER_ARRIVED: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        PICKED_UP: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        IN_PROGRESS: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr || "—";
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const TripsRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [filters, setFilters] = useState<TripsQueryParams>({
        page: 0,
        limit: 20,
        status: "",
        dateFrom: "",
        dateTo: "",
        hasDriverRating: "",
        hasPassengerRating: "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [actionMenu, setActionMenu] = useState<string | null>(null);
    const [cancelModal, setCancelModal] = useState<{ id: string; passengerName: string; driverName: string } | null>(null);
    const [completeModal, setCompleteModal] = useState<{ id: string; passengerName: string; driverName: string; estimatedFare: number; currency: string } | null>(null);
    const [actionReason, setActionReason] = useState("");
    const [finalFare, setFinalFare] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["trips", filters],
        queryFn: () => getTrips(filters),
    });

    const cancelMutation = useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelTrip(id, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["trips"] });
            setCancelModal(null);
            setActionReason("");
        },
    });

    const completeMutation = useMutation({
        mutationFn: ({ id, reason, fare }: { id: string; reason: string; fare?: number }) =>
            forceCompleteTrip(id, reason, fare),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["trips"] });
            setCompleteModal(null);
            setActionReason("");
            setFinalFare("");
        },
    });

    const trips = data?.data?.trips ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;
    const canManageTrips = hasPermission(SECTION_PERMISSIONS.TRIPS_MANAGE[0]);

    const updateFilter = (key: keyof TripsQueryParams, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Trips</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {totalElements} trip{totalElements !== 1 ? "s" : ""} total
                </p>
            </div>

            {/* Search & Filters */}
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <select
                        value={filters.status}
                        onChange={(e) => updateFilter("status", e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                        {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowFilters((p) => !p)}
                        className={showFilters ? "border-primary-500 text-primary-600" : ""}
                    >
                        <FunnelIcon className="h-4 w-4" />
                    </Button>
                    <div className="flex-1" />
                </div>

                {showFilters && (
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">From</label>
                            <input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300" />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">To</label>
                            <input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300" />
                        </div>
                        <select
                            value={filters.hasDriverRating === "" ? "" : String(filters.hasDriverRating)}
                            onChange={(e) => updateFilter("hasDriverRating", e.target.value)}
                            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                            <option value="">Driver Rating</option>
                            <option value="true">Has Rating</option>
                            <option value="false">No Rating</option>
                        </select>
                        <select
                            value={filters.hasPassengerRating === "" ? "" : String(filters.hasPassengerRating)}
                            onChange={(e) => updateFilter("hasPassengerRating", e.target.value)}
                            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                            <option value="">Passenger Rating</option>
                            <option value="true">Has Rating</option>
                            <option value="false">No Rating</option>
                        </select>
                        <Button variant="ghost" size="sm"
                            onClick={() => setFilters({ page: 0, limit: 20, status: "", dateFrom: "", dateTo: "", hasDriverRating: "", hasPassengerRating: "" })}
                        >
                            Clear All
                        </Button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                    </div>
                ) : error ? (
                    <div className="px-6 py-20 text-center">
                        <XCircleIcon className="mx-auto h-10 w-10 text-rose-400" />
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{extractApiError(error)}</p>
                    </div>
                ) : trips.length === 0 ? (
                    <div className="px-6 py-20 text-center">
                        <ClockIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">No trips found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Passenger</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Driver</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Route</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Seats</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Fare</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Distance</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Ratings</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                                    {canManageTrips ? (
                                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {trips.map((trip) => {
                                    const isActive = ["PENDING", "STARTED", "DRIVER_ARRIVED", "PICKED_UP", "IN_PROGRESS"].includes(trip.status);
                                    return (
                                        <tr
                                            key={trip.tripId}
                                            onClick={() => navigate(`/ride-bookings-trips/trips/${trip.tripId}`)}
                                            className="cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{trip.passengerName}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{trip.driverName}</td>
                                            <td className="px-4 py-3">
                                                <div className="max-w-[180px]">
                                                    <div className="flex items-center gap-1.5">
                                                        <MapPinIcon className="h-3 w-3 shrink-0 text-emerald-500" />
                                                        <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-300">{trip.pickupAddress}</p>
                                                    </div>
                                                    <div className="mt-0.5 flex items-center gap-1.5">
                                                        <MapPinIcon className="h-3 w-3 shrink-0 text-rose-500" />
                                                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{trip.dropoffAddress}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center text-sm font-medium text-slate-700 dark:text-slate-300">{trip.numberOfSeats}</td>
                                            <td className="px-4 py-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(trip.finalFare ?? trip.estimatedFare, trip.estimatedFareCurrency)}</p>
                                                    {trip.finalFare !== null && trip.finalFare !== trip.estimatedFare && (
                                                        <p className="text-xs text-slate-400 line-through">{formatCurrency(trip.estimatedFare, trip.estimatedFareCurrency)}</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                                    {trip.distanceKm !== null ? `${trip.distanceKm.toFixed(1)} km` : "—"}
                                                    {trip.durationMinutes !== null && (
                                                        <p className="text-xs text-slate-400">{trip.durationMinutes} min</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3 text-xs">
                                                    {trip.driverRating !== null && (
                                                        <div className="flex items-center gap-0.5" title="Driver rating">
                                                            <StarIcon className="h-3.5 w-3.5 text-amber-400" />
                                                            <span className="font-medium text-slate-600 dark:text-slate-400">{trip.driverRating}</span>
                                                        </div>
                                                    )}
                                                    {trip.passengerRating !== null && (
                                                        <div className="flex items-center gap-0.5" title="Passenger rating">
                                                            <UserCircleIcon className="h-3.5 w-3.5 text-blue-400" />
                                                            <span className="font-medium text-slate-600 dark:text-slate-400">{trip.passengerRating}</span>
                                                        </div>
                                                    )}
                                                    {trip.driverRating === null && trip.passengerRating === null && (
                                                        <span className="text-slate-400">—</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3"><StatusBadge status={trip.status} /></td>
                                            {canManageTrips ? (
                                                <td className="px-4 py-3 text-right">
                                                    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={() => setActionMenu(actionMenu === trip.tripId ? null : trip.tripId)}
                                                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                                                        >
                                                            <EllipsisVerticalIcon className="h-5 w-5" />
                                                        </button>
                                                        {actionMenu === trip.tripId && (
                                                            <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                                                <button
                                                                    onClick={() => { navigate(`/ride-bookings-trips/trips/${trip.tripId}`); setActionMenu(null); }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                                                                >
                                                                    <ClockIcon className="h-4 w-4" />View Details
                                                                </button>
                                                                {isActive ? (
                                                                    <>
                                                                        <button
                                                                            onClick={() => {
                                                                                setCompleteModal({ id: trip.tripId, passengerName: trip.passengerName, driverName: trip.driverName, estimatedFare: trip.estimatedFare, currency: trip.estimatedFareCurrency });
                                                                                setActionMenu(null);
                                                                            }}
                                                                            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                                                        >
                                                                            <CheckCircleIcon className="h-4 w-4" />Force Complete
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                setCancelModal({ id: trip.tripId, passengerName: trip.passengerName, driverName: trip.driverName });
                                                                                setActionMenu(null);
                                                                            }}
                                                                            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                                        >
                                                                            <XCircleIcon className="h-4 w-4" />Cancel Trip
                                                                        </button>
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
                        <p className="text-sm text-slate-600 dark:text-slate-400">Page {currentPage + 1} of {totalPages}</p>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setFilters((p) => ({ ...p, page: currentPage - 1 }))}>
                                <ChevronLeftIcon className="mr-1 h-4 w-4" />Previous
                            </Button>
                            <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setFilters((p) => ({ ...p, page: currentPage + 1 }))}>
                                Next<ChevronRightIcon className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(null); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Trip</h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Cancel trip between <span className="font-medium text-slate-700 dark:text-slate-300">{cancelModal.passengerName}</span> and{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">{cancelModal.driverName}</span>?
                        </p>
                        <textarea rows={3} placeholder="Reason..." value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(null); setActionReason(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={!actionReason.trim() || cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate({ id: cancelModal.id, reason: actionReason })}
                            >
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Trip"}
                            </Button>
                        </div>
                        {cancelMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>}
                    </div>
                </div>
            )}

            {/* Force Complete Modal */}
            {completeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCompleteModal(null); setActionReason(""); setFinalFare(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Force Complete Trip</h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Force complete trip for <span className="font-medium text-slate-700 dark:text-slate-300">{completeModal.passengerName}</span>.
                            Estimated fare: <span className="font-semibold">{formatCurrency(completeModal.estimatedFare, completeModal.currency)}</span>
                        </p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-slate-500">Final Fare (leave empty to use estimated)</label>
                                <input type="number" placeholder={completeModal.estimatedFare.toString()} value={finalFare} onChange={(e) => setFinalFare(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <textarea rows={3} placeholder="Reason..." value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCompleteModal(null); setActionReason(""); setFinalFare(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={completeMutation.isPending}
                                onClick={() => completeMutation.mutate({ id: completeModal.id, reason: actionReason, fare: finalFare ? parseFloat(finalFare) : undefined })}
                            >
                                {completeMutation.isPending ? "Completing..." : "Force Complete"}
                            </Button>
                        </div>
                        {completeMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(completeMutation.error)}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
